import {useCallback, useEffect, useState} from 'react';

export interface UsePendingImageUploadParams {
  maxBytes: number;
  allowedMimeTypes: string[];
  currentUrl?: string;
  tooLargeMessage: string;
  unsupportedTypeMessage: string;
}

export interface PendingImageUpload {
  previewUrl: string | undefined;
  pendingFile: File | null;
  isRemoved: boolean;
  error: string | undefined;
  /** True once the user has picked a new file or explicitly removed the current one. */
  hasChange: boolean;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  /** Clears all pending state — call when the owning form resets (e.g. modal reopen). */
  reset: () => void;
  /** Surfaces a server-side rejection (e.g. a race with a stale client-side size limit). */
  setError: (message: string | undefined) => void;
}

/**
 * Manages a pending image upload's local state: the selected `File`, its object-URL preview,
 * an explicit "remove the current image" flag, and inline validation against size/mime
 * constraints. Fully controlled and I/O-free — the caller owns the actual upload request.
 * Shared by every avatar/logo upload (account-settings, firm-settings, ops-web create-org), which
 * differ only in their constants and copy.
 */
export function usePendingImageUpload({
  maxBytes,
  allowedMimeTypes,
  currentUrl,
  tooLargeMessage,
  unsupportedTypeMessage
}: UsePendingImageUploadParams): PendingImageUpload {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isRemoved, setIsRemoved] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Derives the preview URL from the selected file — the only place its lifecycle is managed,
  // instead of pairing a manual create/revoke at every call site that sets `pendingFile`. The
  // cleanup fires on both file change and unmount, so a picked-then-abandoned file can't leak.
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!pendingFile) {
      setLocalPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setLocalPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const onFileSelect = useCallback(
    (file: File) => {
      if (!allowedMimeTypes.includes(file.type)) {
        setError(unsupportedTypeMessage);
        return;
      }
      if (file.size > maxBytes) {
        setError(tooLargeMessage);
        return;
      }

      setError(undefined);
      setIsRemoved(false);
      setPendingFile(file);
    },
    [allowedMimeTypes, maxBytes, tooLargeMessage, unsupportedTypeMessage]
  );

  const onRemove = useCallback(() => {
    setPendingFile(null);
    setIsRemoved(true);
    setError(undefined);
  }, []);

  const reset = useCallback(() => {
    setPendingFile(null);
    setIsRemoved(false);
    setError(undefined);
  }, []);

  return {
    previewUrl: localPreviewUrl ?? (isRemoved ? undefined : currentUrl),
    pendingFile,
    isRemoved,
    error,
    hasChange: pendingFile !== null || isRemoved,
    onFileSelect,
    onRemove,
    reset,
    setError
  };
}
